import React, { FC, useCallback, useEffect, useState } from "react";
import { Box, Button, Image, Spinner, Typography } from "@orion/ui";
import { LoginScreen } from "../types";
import { PasswordResetScreen } from "@delorean/forgetPassword";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { setScreen } from "@delorean/actions/loginActions";
import { abortPolling } from "@delorean/actions/authorizeActions";
import { useCodeVerification, useLoginForm } from "../hooks";
import { AppDomain } from "@delorean/_shared/constants";
import { Close } from "@orion/icons";
import { useTranslation } from "react-i18next";

export enum LocationType {
  LOGIN = "LOGIN",
  RESET_PSW = "RESET_PSW",
}
type Props = {
  location: LocationType;
};
export const NotificationScreen: FC<Props> = ({ location }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    submitLoginForQrCode,
    loading: loginLoading,
    submitNewNotify,
    submitNewNotifyFromChangePassword,
    submitResetPasswordForQrCode,
  } = useLoginForm();
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const { submitPolling, rollbackToLogin, loading } =
    useCodeVerification();
  const NOTIFICATION_DURATION = 60;
  
  const handleNewNotification = () => {
    // Abortisci il polling precedente prima di avviarne uno nuovo
    dispatch(abortPolling());
    //questo componente è usato in entrambi i flow di login e resetPsw per eseguire la SCA con notifica push
    if (location === LocationType.LOGIN) {
      submitNewNotify();
    } else {
      submitNewNotifyFromChangePassword();
    }
    setIsDisabled(true);
    setCountdown(NOTIFICATION_DURATION);
    submitPolling();
  };
  const handleQrCodeRequest = useCallback(() => {
    // Abortisci il polling quando si richiede il QR code
    dispatch(abortPolling());
    if (location === LocationType.LOGIN) {
      submitLoginForQrCode();
    } else {
      submitResetPasswordForQrCode();
    }
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => (prevCountdown ?? 0) - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setIsDisabled(false);
      setCountdown(null);
    }
  }, [countdown]);

  useEffect(() => {
    setIsDisabled(true);
    setCountdown(NOTIFICATION_DURATION);
    submitPolling();
  }, []);

  return (
    <Box flex column alignItems="center" gap13 maxWidth={"3xl"}>
      {loading ? (
        <Image
          alt="Image"
          height={80}
          placeholder="LSHny%kXTenhPqRlbwWB0gaKn3kW"
          src=".\src\_shared\assets\images\waitingTime.png"
          width={80}
        />
      ) : (
        <Box
          flex
          backgroundColor="error100"
          borderRadius="full"
          w20
          h20
          alignItems="center"
          justifyContent="center"
        >
          <Close w15 color="white" />
        </Box>
      )}
      <Box flex column gap7>
        <Typography fontBold text2xl textAlign="center">
          {loading ? t("login.receiveNotify") : t("login.notifyExpired")}
        </Typography>
        <Typography textAlign="center" style={{ whiteSpace: "pre-line" }}>
          {loading
            ? t("login.notifyInstructions", { appName: AppDomain.BPER })
            : t("login.notifyExpiredInstructions").replace(/\. /g, ".\n")}
        </Typography>
      </Box>
      {loading && (
        <Typography textSm>{t("login.noNotificationQuestion")}</Typography>
      )}
      <Box flex flexDirection="column" alignItems="flex-start" gap9>
        <Box flex justifyItems="center" gap9>
          {/* new notification button */}
          <Box>
            <Button
              variant="outlinePrimary"
              minWidth={"48"}
              maxWidth={"48"}
              onPress={handleNewNotification}
              isDisabled={isDisabled}
            >
              <Typography
                fontSize="md"
                lineHeight="md"
                aria-hidden={true}
                style={{ textWrap: "nowrap" }}
              >
                {t("login.authFailure.sendNewCode")}
              </Typography>
            </Button>
          </Box>

          <Box>
            <Button
              variant="outlinePrimary"
              minWidth={"48"}
              maxWidth={"48"}
              type="submit"
              onPress={handleQrCodeRequest}
            >
              {loginLoading ? (
                <Spinner size={"9"} />
              ) : (
                <Typography fontSize="md" lineHeight="md" aria-hidden={true}>
                  {t("login.generateQRCode")}
                </Typography>
              )}
            </Button>
          </Box>
        </Box>

        {/* countdown */}
        {isDisabled && countdown !== null && (
          <Typography textXs>
            {t("login.newNotificationRequest", { countdown })}
          </Typography>
        )}
      </Box>

      <Box flex alignItems="center" justifyCenter gap4>
        <Typography mt8 textAlign="center" textNeutral70>
          {t("login.authFailure.cancelProcedure")}
        </Typography>
        <Button
          variant={"ghostPrimary"}
          p0
          onClick={() => rollbackToLogin()}
        >
          <Typography
            mt8
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {t("login.cancel")}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationScreen;
